import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon, ZoomInIcon, ZoomOutIcon } from 'lucide-react';
import type { LookInsidePage } from '../data/lookInsidePages';
type PageLightboxProps = {
  page: LookInsidePage;
  onClose: () => void;
};
const EASE = [0.23, 1, 0.32, 1] as const;

/** Full-screen page viewer: tap to zoom, close with X, Esc or swipe down. */
export function PageLightbox({
  page,
  onClose
}: PageLightboxProps) {
  const [zoomed, setZoomed] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const dragged = useRef(false);
  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [onClose]);
  const iconButton = 'flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 ease-out hover:bg-[#F2694B] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#F2694B]/60';
  return <motion.div role="dialog" aria-modal="true" aria-label={page.caption} className="fixed inset-0 z-[100] flex flex-col bg-[#1E2140]/95 font-sans" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: EASE
  }} data-id="element-199" data-mp="hi2spwfs17ms">
      <div className="flex items-center justify-between gap-3 px-4 py-3" data-id="element-200" data-mp="va0u1l38xz8e">
        <p className="line-clamp-1 text-sm text-white/85" data-id="element-201" data-mp="vbbubaxhgour">{page.caption}</p>
        <div className="flex flex-none gap-2" data-id="element-202" data-mp="3uwqqv9wca1md">
          <button type="button" onClick={() => setZoomed(z => !z)} aria-label={zoomed ? 'Zoom out' : 'Zoom in'} className={iconButton} data-id="element-203" data-mp="2soeob19a212">
            {zoomed ? <ZoomOutIcon className="h-5 w-5" aria-hidden="true" data-id="element-204" data-mp="1147ti3qhid4h" /> : <ZoomInIcon className="h-5 w-5" aria-hidden="true" data-id="element-205" data-mp="1fu6pq0yhbehb" />}
          </button>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className={iconButton} data-id="element-206" data-mp="1aukc3qeqnw7z">
            <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-207" data-mp="i6eq70by816k" />
          </button>
        </div>
      </div>

      <motion.div className="relative min-h-0 flex-1" drag={zoomed ? false : 'y'} dragConstraints={{
      top: 0,
      bottom: 0
    }} dragElastic={{
      top: 0.05,
      bottom: 0.6
    }} onDragStart={() => {
      dragged.current = true;
    }} onDragEnd={(_, info) => {
      if (info.offset.y > 120 || info.velocity.y > 600) onClose();
    }} initial={{
      scale: 0.96
    }} animate={{
      scale: 1
    }} exit={{
      scale: 0.96
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} data-id="element-208" data-mp="w6fpwcgh92kg">
        <div className={zoomed ? 'h-full w-full overflow-auto overscroll-contain' : 'flex h-full w-full items-center justify-center px-4 pb-4'} data-id="element-209" data-mp="2g3ojlsozu9o4">
          <img src={page.src} alt={page.caption} draggable={false} onPointerDown={() => {
          dragged.current = false;
        }} onClick={() => {
          if (!dragged.current) setZoomed(z => !z);
        }} className={zoomed ? 'mx-auto block h-auto w-[1125px] max-w-none cursor-zoom-out' : 'max-h-full max-w-full cursor-zoom-in select-none rounded-xl object-contain'} data-id="element-210" data-mp="2645z9b568lvj" />
        </div>
      </motion.div>

      <p className="pb-4 text-center text-xs text-white/60" data-id="element-211" data-mp="3hk4ddtf7hy3m">
        {zoomed ? 'Scroll to move around · Tap to zoom out' : 'Tap the page to zoom · Swipe down to close'}
      </p>
    </motion.div>;
}